import React, {DragEvent} from 'react';
import {NavLink, useParams} from 'react-router-dom';

import CreateIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import {
  Breadcrumbs,
  Button,
  Grid,
  IconButton,
  TextField,
  Typography,
} from '@mui/material';
import {AxiosError} from 'axios';

import {
  apiCall,
  getScreenshotUrl,
  hideLoader,
  showLoader,
  YesNoDialog,
} from '../../utils';

import {ProjectScreenshotDialog} from './ProjectScreenshotDialog';

import {
  ProjectScreenshotsDTO,
  ScreenshotKeyConnectionDTO,
} from '@bumpy/i18n_common';

export const ProjectScreenshots: React.FC = () => {
  const {projectUuid} = useParams();
  const [search, setSearch] = React.useState('');
  const [deleteScreenshot, setDeleteScreenshot] = React.useState<
    string | undefined
  >(undefined);
  const [dto, setDto] = React.useState<ProjectScreenshotsDTO>();
  const avatarFileInput = React.useRef<HTMLInputElement>(null);
  const [activeScreenshot, setActiveScreenshot] = React.useState<
    string | undefined
  >();
  const [isDragging, setIsDragging] = React.useState(false);

  function onDragEnter(e: DragEvent): void {
    e.preventDefault();
    e.stopPropagation();

    setIsDragging(true);
  }

  function onDragLeave(e: DragEvent): void {
    e.preventDefault();
    e.stopPropagation();
    if (
      e.currentTarget === e.target ||
      !e.currentTarget.contains(e.relatedTarget as Node)
    ) {
      setIsDragging(false);
    }
  }

  function onDragOver(e: DragEvent): void {
    e.preventDefault();
    e.stopPropagation();
  }

  async function onDrop(e: DragEvent): Promise<void> {
    e.preventDefault();
    e.stopPropagation();

    const newDto = {
      ...dto!,
      screenshots: [...dto!.screenshots],
    };
    const files: File[] = Array.from(e.dataTransfer.files);
    for (let i = 0; i < files.length; ++i) {
      const file = files[i];
      showLoader(`Uploading "${file.name}", ${i + 1} / ${files.length}`);
      try {
        await uploadFile(file);
        newDto.screenshots.push(file.name);
      } catch {}
    }
    hideLoader();
    newDto.screenshots.sort();
    setDto(newDto);
    setIsDragging(false);
  }

  function uploadFile(file: File): Promise<void> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (upload) => {
        if (!upload.target) {
          reject();
          return;
        }

        apiCall({
          method: 'POST',
          url: `/projects/${projectUuid}/screenshots/${file.name}`,
          data: upload.target.result,
          secure: true,
        })
          .then(() => resolve())
          .catch((e) => {
            if (e instanceof AxiosError && e.response) {
              const {data} = e.response;
              if (data.err === 'e_screenshot_already_exists') {
                alert(`Such screenshot "${file.name}" already exists`);
                reject();
                return;
              }
            }
            alert('Unknown error during saving');
            reject();
          });
      };
      reader.onerror = () => {
        alert(`Error of reading file "${file.name}"`);
        reject();
      };
      reader.readAsArrayBuffer(file);
    });
  }

  React.useEffect(() => {
    apiCall<ProjectScreenshotsDTO>({
      method: 'GET',
      url: `/projects/${projectUuid}/screenshots`,
      secure: true,
    }).then(({data}) => {
      setDto(data);
    });
  }, [projectUuid]);

  let list: string[] = [];
  if (dto) {
    list.push(...dto.screenshots);
  }
  if (search.length > 0) {
    const s = search.toLowerCase();
    list = list.filter((i) => i.toLowerCase().includes(s));
  }

  return (
    <Grid
      container
      spacing={2}
      onDragEnter={onDragEnter}
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
      onDrop={onDrop}
      style={{background: isDragging ? '#ccc' : undefined}}
    >
      <Grid item xs={6}>
        <Breadcrumbs aria-label="breadcrumb">
          <Breadcrumbs aria-label="breadcrumb">
            <Breadcrumbs aria-label="breadcrumb">
              <NavLink to="/">Projects</NavLink>
              <NavLink to={`/${projectUuid}`}>{dto?.name}</NavLink>
              <Typography color="secondary">Screenshots</Typography>
            </Breadcrumbs>
          </Breadcrumbs>
        </Breadcrumbs>
      </Grid>
      <Grid item xs={4}>
        <TextField
          label="Search"
          margin="none"
          size="small"
          id="search-input"
          sx={{width: 270}}
          value={search}
          onChange={(event) => {
            setSearch(event.target.value);
          }}
        />
      </Grid>
      <Grid item xs={2}>
        <Button
          variant="contained"
          color="primary"
          onClick={() => {
            avatarFileInput.current?.click();
          }}
          startIcon={<CreateIcon />}
        >
          Create
        </Button>
      </Grid>
      <Grid item xs={12}>
        <input
          type="file"
          multiple={false}
          ref={avatarFileInput}
          style={{display: 'none'}}
          accept={'image/*'}
          onChange={(event) => {
            if (!event.target.files || event.target.files.length === 0) {
              return;
            }
            showLoader('Uploading screenshot');
            const file = event.target.files[0];
            uploadFile(file)
              .then(() => {
                setDto({
                  ...dto!,
                  screenshots: [...dto!.screenshots, file.name].sort(),
                });
              })
              .catch(() => {})
              .finally(hideLoader);
          }}
        />
      </Grid>
      {list.map((screenshot) => (
        <Grid item xs={3} key={screenshot}>
          <div style={{position: 'relative'}}>
            <span
              style={{
                position: 'absolute',
                left: 0,
                top: 0,
                background: '#ccc',
                padding: 5,
                fontSize: 12,
                fontWeight: 'bold',
                color: '#151515',
              }}
            >
              {
                dto?.keysConnections.filter((c) => c.screenshot === screenshot)
                  .length
              }
            </span>
            <img
              alt={screenshot}
              src={getScreenshotUrl(projectUuid!, screenshot)}
              width="100%"
              height={300}
              style={{objectFit: 'cover', borderRadius: 4}}
              onClick={() => {
                setActiveScreenshot(screenshot);
              }}
            />

            <IconButton
              color="error"
              sx={{position: 'absolute', top: 0, right: 0}}
              onClick={() => {
                setDeleteScreenshot(screenshot);
              }}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </div>
          <Typography fontSize={12} align="center" justifyContent="flex-end">
            {screenshot}
          </Typography>
        </Grid>
      ))}
      {deleteScreenshot && (
        <YesNoDialog
          title="Are you sure?"
          message={`You want to delete ${deleteScreenshot} ?`}
          onYes={() => {
            setDeleteScreenshot(undefined);
            apiCall({
              method: 'DELETE',
              url: `/projects/${projectUuid}/screenshots/${deleteScreenshot}`,
              secure: true,
            }).then(() => {
              setDto({
                ...dto!,
                screenshots: dto!.screenshots.filter(
                  (s) => s !== deleteScreenshot,
                ),
              });
              setDeleteScreenshot(undefined);
            });
          }}
          onNo={() => {
            setDeleteScreenshot(undefined);
          }}
        />
      )}
      {activeScreenshot && dto && (
        <ProjectScreenshotDialog
          screenshotName={activeScreenshot}
          connections={dto.keysConnections.filter(
            (c) => c.screenshot === activeScreenshot,
          )}
          keysStatuses={dto.keysStatuses}
          projectUuid={projectUuid!}
          onClose={() => {
            setActiveScreenshot(undefined);
          }}
          onSave={(newName, connections) => {
            const newConnections = dto.keysConnections.filter(
              (c) => c.screenshot !== activeScreenshot,
            );
            newConnections.push(...connections);

            const data: ScreenshotKeyConnectionDTO = {
              keysConnections: newConnections,
            };
            if (newName !== activeScreenshot) {
              dto.screenshots[dto.screenshots.indexOf(activeScreenshot)] =
                newName;
              data.rename = {
                from: activeScreenshot,
                to: newName,
              };
            }

            showLoader();
            apiCall({
              method: 'PUT',
              url: `/projects/${projectUuid}/screenshots`,
              secure: true,
              data,
            })
              .then(() => {
                setDto({
                  ...dto,
                  keysConnections: newConnections,
                });
                setActiveScreenshot(undefined);
              })
              .finally(hideLoader);
          }}
        />
      )}
    </Grid>
  );
};
